import { useEffect } from 'react';
import { Box } from '@mui/material';
import { BaseRecord, HttpError, useCreate, useGo } from '@refinedev/core';
import { Create, useAutocomplete } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { isEqual } from 'lodash';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteInput } from '@attackiq/components/inputs/AutocompleteInput';
import { ATTACKIQ_ID, MODULE_CONTENT_MAP } from '@attackiq/constants/platform';

import useSearchParams from '../../hooks/useSearchParams';
import { ProjectTemplate, Scenario, AttackGraphTemplate, FlexPackage, Module } from '../../types';

// `blueprinttemplate` is deliberately excluded: blueprints are retired, so no new blueprint
// content can be authored. The key stays in MODULE_CONTENT_MAP because module_contents rows
// created before the retirement still render their label through it (list.tsx, modules/show.tsx).
const contentTypeOptions = Object.entries(MODULE_CONTENT_MAP)
  .filter(([model]) => model !== 'blueprinttemplate')
  .map(([key, value]) => ({
    model: key,
    displayName: value
  }));

/**
 * `module` and `content_type` are nullable because their inputs render with
 * `defaultValue={null}` and neither carries a `required` rule; `content` is the
 * `{ id, label }` list that `ContentInput` builds for its multi-select.
 */
type ModuleContentFormValues = {
  module: Module | null;
  content_type: (typeof contentTypeOptions)[number] | null;
  content: { id: string; label: string }[];
};

const ModuleContentCreate = () => {
  const { saveButtonProps, ...formMethods } = useForm<BaseRecord, HttpError, ModuleContentFormValues>({
    refineCoreProps: {
      resource: 'module_contents',
      action: 'create',
      redirect: false
    }
  });
  const go = useGo();
  const { mutateAsync: createAsync } = useCreate();

  const { handleSubmit } = formMethods;

  const onSubmit = handleSubmit(async ({ content, content_type, module }) => {
    // Nothing marks these required, so both are still null until picked.
    if (!module || !content_type) {
      return;
    }

    const promises = content.map(async content => {
      await createAsync({
        resource: 'module_contents',
        values: {
          module_id: module.id,
          content_id: content.id,
          content_type_model: content_type.model,
          company_id: ATTACKIQ_ID,
          content_type_app_label: 'ai_cloud'
        }
      });
    });

    await Promise.all(promises);
    go({
      to: {
        resource: 'modules',
        action: 'show',
        id: module.id
      }
    });
  });

  return (
    <FormProvider {...formMethods}>
      <form name="create-module-content-form" onSubmit={onSubmit}>
        <Create saveButtonProps={{ type: 'submit' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ModuleInput />
            <ContentTypeInput />
            <ContentInput />
          </Box>
        </Create>
      </form>
    </FormProvider>
  );
};

const ModuleInput = () => {
  const { setValue } = useFormContext();
  const { autocompleteProps } = useAutocomplete<Module>({
    resource: 'modules',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });
  const { options } = autocompleteProps;

  const { module_id } = useSearchParams();

  useEffect(() => {
    if (module_id && options) {
      const currentModule = options.find(option => option.id === module_id) || null;

      setValue('module', currentModule);
    }
  }, [setValue, module_id, options]);

  return (
    <AutocompleteInput
      {...autocompleteProps}
      options={[...autocompleteProps.options]}
      sx={{ mt: 0.5 }}
      name="module"
      defaultValue={null}
      getOptionLabel={option => option.display_name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{
        label: 'Module'
      }}
    />
  );
};

const ContentTypeInput = () => {
  const contentType = useWatch({ name: 'content_type', defaultValue: null });
  const { setValue } = useFormContext();

  return (
    <AutocompleteInput
      name="content_type"
      defaultValue={null}
      options={contentTypeOptions}
      onChange={(_event, value) => {
        if (!isEqual(value, contentType)) {
          setValue('content', []);
        }
      }}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;

        return option.displayName || '';
      }}
      isOptionEqualToValue={(option, value) => option.model === value.model}
      TextFieldProps={{
        label: 'Content Type'
      }}
    />
  );
};

const ContentInput = () => {
  const contentType: (typeof contentTypeOptions)[number] = useWatch({ name: 'content_type', defaultValue: null });
  const isScenario = contentType?.model === 'scenario';
  const isAssessmentTemplate = contentType?.model === 'projecttemplate';
  const isAttackGraphTemplate = contentType?.model === 'attackgraphtemplate';
  const isFlexPackage = contentType?.model === 'flexpackage';
  const { autocompleteProps: scenariosAutocompleteProps } = useAutocomplete<Scenario>({
    resource: 'scenarios',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: templatesAutocompleteProps } = useAutocomplete<ProjectTemplate>({
    resource: 'assessment_templates',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: attackgraphTemplatesAutocompleteProps } = useAutocomplete<AttackGraphTemplate>({
    resource: 'attack_graph_templates',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const { autocompleteProps: FlexPackageAutocompleteProps } = useAutocomplete<FlexPackage>({
    resource: 'flex_packages',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const mapOptions = (options: readonly { id: string; name?: string; template_name?: string }[]) => {
    return options.map(option => ({ id: option.id, label: option.name || option.template_name || '' }));
  };

  const getActiveAutocompleteProps = () => {
    if (isScenario) return scenariosAutocompleteProps;
    if (isAssessmentTemplate) return templatesAutocompleteProps;
    if (isAttackGraphTemplate) return attackgraphTemplatesAutocompleteProps;
    if (isFlexPackage) return FlexPackageAutocompleteProps;
    return null;
  };

  const activeProps = getActiveAutocompleteProps();
  const options = activeProps
    ? isFlexPackage
      ? FlexPackageAutocompleteProps.options.map(option => ({
          id: option.id,
          label: [option.name, option.os, option.arch].filter(Boolean).join(' - ')
        }))
      : mapOptions(activeProps.options)
    : [];

  return (
    <AutocompleteInput
      options={options}
      onInputChange={activeProps?.onInputChange}
      name="content"
      defaultValue={[]}
      disabled={!contentType}
      getOptionLabel={option => option.label || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{ label: 'Content' }}
      multiple
    />
  );
};

export default ModuleContentCreate;
